import { create } from 'zustand';

const FETCH_TTL_MS = 30_000;

/**
 * Single source of truth for earn-program state. Previously each consumer
 * (checklist hook ×N instances, Earn Credits settings) called
 * accountApi.getEarnPrograms() directly, producing several redundant
 * /json/user/earn-programs requests on every page load. fetchEarnPrograms()
 * collapses concurrent and rapid-repeat callers into one in-flight request
 * and serves a short-TTL cache to the rest.
 *
 * bumpEarnPrograms() (called by the socket on earn_credited) now triggers a
 * forced refetch so granted credits reflect immediately.
 */
export const useEarnProgramsRefreshStore = create((set, get) => ({
  nonce: 0,
  programs: [],
  isLoading: false,
  _promise: null,
  _fetchedAt: 0,

  bumpEarnPrograms: () => {
    set((s) => ({ nonce: s.nonce + 1 }));
    get().fetchEarnPrograms({ force: true }).catch(() => {});
  },

  fetchEarnPrograms: async ({ force = false } = {}) => {
    const state = get();
    if (state._promise) return state._promise;
    if (!force && state.programs && Date.now() - state._fetchedAt < FETCH_TTL_MS) {
      return state.programs;
    }

    const promise = (async () => {
      try {
        const { accountApi } = require('@/lib/api');
        const programs = await accountApi.getEarnPrograms();
        set({ programs, _fetchedAt: Date.now(), isLoading: false, _promise: null });
        return programs;
      } catch (err) {
        set({ isLoading: false, _promise: null });
        throw err;
      }
    })();

    set({ _promise: promise, isLoading: true });
    return promise;
  },
}));
